/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { IPublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { ThemeProvider } from "@mui/material";
import React, { useContext, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ProfileComponent from "./components/accountSettings/profile";
import SecurityComponent from "./components/accountSettings/security";
import { AuthRoute } from "./components/authRoute";
import ErrorBoundary from "./components/errorBoundary";
import GlobalErrorController from "./components/globalErrorController";
import Header from "./components/header";
import { AccountSettings } from "./routes/accountSettings";
import LoginPage from "./routes/login";
import MainPage from "./routes/main";
import NotFound from "./routes/notFound";
import PrivacyPolicyPage from "./routes/privacyPolicy";
import RegisterPage from "./routes/register";
import TermsOfServicePage from "./routes/termsOfService";
import ServiceWrapper, { UserContext } from "./ServiceWrapper";
import "./styles/App.css";
import { createTheme, StyledEngineProvider } from "@mui/material/styles";
import CancelPage from "./routes/cancel";
import ReactTooltip from "react-tooltip";
import SubscriptionComponent from "./components/accountSettings/subscription";
import { UserRegisteredRoute } from "./components/userRegisteredRoute";
import { CreateContentRoute } from "./components/createContentRoute";
import { CreateContent } from "./routes/createContent";
import DraftsComponent from "./components/createContent/drafts";

declare module "@mui/material/styles" {
  interface BreakpointOverrides {
    mobile: true; // adds the `mobile` breakpoint
    desktop: true;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      // light: will be calculated from palette.primary.main,
      main: "#0096ff",
      // dark: will be calculated from palette.primary.main,
      contrastText: "white",
    },
    secondary: {
      main: "#ff7f01",
    },
    // Used by `getContrastText()` to maximize the contrast between
    // the background and the text.
    contrastThreshold: 3,
    // Used by the functions below to shift a color's luminance by approximately
    // two indexes within its tonal palette.
    // E.g., shift from Red 500 to Red 300 or Red 700.
    tonalOffset: 0.2,
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      mobile: 600,
      desktop: 1201,
    },
  },
});

const MainContent = () => {
  const { headerFunction, setHeaderFunction } = useContext(UserContext);

  return (
    <div className="App">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      {headerFunction}
      <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <div className="contentWidth">
          <Routes>
            <Route path="/cancel" element={<CancelPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/privacyPolicy" element={<PrivacyPolicyPage />} />
            <Route path="/termsOfService" element={<TermsOfServicePage />} />
            <Route
              path="/accountSettings"
              element={
                <AuthRoute>
                  <UserRegisteredRoute>
                    <AccountSettings />
                  </UserRegisteredRoute>
                </AuthRoute>
              }
            >
              <Route index element={<ProfileComponent />} />
              <Route path="profile" element={<ProfileComponent />} />
              <Route path="security" element={<SecurityComponent />} />
              <Route path="subscription" element={<SubscriptionComponent />} />
              <Route path="*" element={<ProfileComponent />} />
            </Route>
            <Route
              path="/createContent"
              element={
                <AuthRoute>
                  <UserRegisteredRoute>
                    <CreateContentRoute>
                     <CreateContent/> 
                    </CreateContentRoute>
                  </UserRegisteredRoute>
                </AuthRoute>
              }
            >
              <Route index element={<DraftsComponent />} />
              <Route path="drafts/:action/:id" element={<DraftsComponent />} />
              <Route path="drafts/:action" element={<DraftsComponent />} />
              <Route path="drafts" element={<DraftsComponent  />} />
              <Route path="*" element={<DraftsComponent />} />
            </Route>
            <Route path="/"
              element={
                <UserRegisteredRoute>
                  <MainPage />
                </UserRegisteredRoute>
              } />
          </Routes>
        </div>
      </div>
    </div>
  );
};

type AppProps = {
  pca: IPublicClientApplication;
};

/**
 * msal-react is built on the React context API and all parts of your app that require authentication must be
 * wrapped in the MsalProvider component. You will first need to initialize an instance of PublicClientApplication
 * then pass this to MsalProvider as a prop. All components underneath MsalProvider will have access to the
 * PublicClientApplication instance via context as well as all hooks and components provided by msal-react. For more, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
export default function App({ pca }: AppProps) {
  //const isProduction = process.env.NODE_ENV === 'production';

  return (
    <MsalProvider instance={pca}>
      <Router>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <ErrorBoundary>
              <GlobalErrorController />
              <ServiceWrapper>
                <MainContent />
              </ServiceWrapper>
            </ErrorBoundary>
          </ThemeProvider>
        </StyledEngineProvider>
      </Router>
    </MsalProvider>
  );
}
